import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import Modal from '../../components/ModalVoltar';
import { api } from '../../lib/api';
import { useToast } from '../../context/ToastContext';

// "Proprietário" do imóvel = um cliente (c1). Igual ao modal do legado:
// lista com busca + "Novo Cliente" (cadastro rápido: nome, telefone, e-mail,
// categoria, WhatsApp) que já seleciona o cliente criado.
// `empresaId` (public_id) só é necessário pro superadmin (nivel 1), que não
// pertence a nenhuma empresa; pros demais o servidor usa a da sessão.
export default function SeletorProprietario({ show, empresaId, aoFechar, aoEscolher }) {
  const toast = useToast();
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState(''); // o que de fato foi pra API (depois da espera)
  const [clientes, setClientes] = useState(null);
  const [total, setTotal] = useState(0);
  const [novo, setNovo] = useState(false);
  const [form, setForm] = useState({ nome: '', celular: '', email: '', categoria: 'Proprietário vendedor', whats: false });
  const [salvando, setSalvando] = useState(false);
  const cache = useRef(new Map()); // mesma busca repetida não vai de novo pra API

  // Espera o usuário PARAR de digitar (1 s) antes de buscar: digitando "5990" a API recebe uma
  // chamada só ("5990"), não "5", "59", "599" e "5990". Enter busca na hora. Menos de 2 caracteres
  // não busca (mostra os cadastrados mais recentes).
  useEffect(() => {
    if (!show) return undefined;
    const limpo = busca.trim();
    const alvo = limpo.length >= 2 ? limpo : '';
    if (alvo === termo) return undefined;
    const espera = setTimeout(() => setTermo(alvo), 1000);
    return () => clearTimeout(espera);
  }, [busca, show, termo]);

  function buscarAgora() {
    const limpo = busca.trim();
    setTermo(limpo.length >= 2 ? limpo : '');
  }

  useEffect(() => {
    if (!show) return undefined;
    const chave = `${empresaId || ''}|${termo}`;
    const guardado = cache.current.get(chave);
    if (guardado) {
      setClientes(guardado.dados);
      setTotal(guardado.total);
      return undefined;
    }
    let cancelado = false;
    setClientes(null);
    const q = new URLSearchParams({ per_page: 100 });
    if (termo) q.set('q', termo);
    if (empresaId) q.set('empresa_id', empresaId);
    api
      .get(`/clientes?${q}`)
      .then((r) => {
        if (cancelado) return;
        cache.current.set(chave, { dados: r.data, total: r.meta?.total ?? r.data.length });
        setClientes(r.data);
        setTotal(r.meta?.total ?? r.data.length);
      })
      .catch((err) => {
        if (!cancelado) {
          setClientes([]);
          toast.erro(err.message || 'Não foi possível carregar os clientes');
        }
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, termo, empresaId]);

  // um cliente recém-criado precisa aparecer na próxima busca
  useEffect(() => {
    if (!show) cache.current.clear();
  }, [show]);

  const aguardando = busca.trim().length >= 2 ? busca.trim() !== termo : termo !== '';
  const palavras = termo.split(/\s+/).filter((p) => p.length >= 2);

  // negrito nos pedaços que casaram (sem acento / sem diferença de maiúscula não é replicado aqui:
  // só destaca o que bate literalmente, é só um apoio visual)
  function destacar(texto) {
    const s = String(texto ?? '');
    if (!palavras.length) return s;
    const re = new RegExp(`(${palavras.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
    return s.split(re).map((parte, i) => (i % 2 === 1 ? <strong key={i} className="bg-warning-subtle">{parte}</strong> : parte));
  }

  async function salvarNovo(e) {
    e.preventDefault();
    if (!form.nome.trim()) {
      toast.erro('Informe o nome do cliente');
      return;
    }
    setSalvando(true);
    try {
      const corpo = {
        c1_nome: form.nome.trim(),
        c1_celular: form.celular,
        c1_email: form.email,
        c1_categoria: form.categoria,
        c1_whats: form.whats ? 'true' : 'false',
      };
      if (empresaId) corpo.c1_empresa = empresaId;
      const r = await api.post('/clientes', corpo);
      toast.sucesso('Cliente cadastrado.');
      setNovo(false);
      setForm({ nome: '', celular: '', email: '', categoria: 'Proprietário vendedor', whats: false });
      aoEscolher({ public_id: r.data.public_id, nome: r.data.c1_nome, celular: r.data.c1_celular });
    } catch (err) {
      toast.erro(err.message || 'Não foi possível cadastrar o cliente');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Modal show={show} onHide={aoFechar} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="h5">{novo ? 'Adicionar cliente' : 'Escolher proprietário'}</Modal.Title>
      </Modal.Header>

      {!novo ? (
        <>
          <Modal.Body>
            <Form.Control
              type="search"
              placeholder="Nome, telefone, e-mail ou CPF/CNPJ..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Enter busca na hora; se já há um único resultado, escolhe ele
                  if (!aguardando && clientes?.length === 1) {
                    const c1 = clientes[0];
                    aoEscolher({ public_id: c1.public_id, nome: c1.c1_nome, celular: c1.c1_celular });
                  } else {
                    buscarAgora();
                  }
                }
              }}
              autoFocus
              autoComplete="off"
            />
            <div className="small text-muted mt-1 mb-3 d-flex justify-content-between flex-wrap gap-1">
              <span>
                {termo || aguardando ? 'Digite várias palavras para refinar (todas precisam aparecer). Telefone: só os números.' : 'Mostrando os cadastrados mais recentes. Digite para buscar.'}
              </span>
              <span>{aguardando || !clientes ? <Spinner animation="border" size="sm" /> : `${total} encontrado(s)${total > clientes.length ? ` — mostrando ${clientes.length}` : ''}`}</span>
            </div>
            {clientes && (
              <Table hover size="sm" responsive style={{ opacity: aguardando ? 0.5 : 1 }}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Telefone</th>
                    <th className="d-none d-md-table-cell">E-mail</th>
                    <th className="d-none d-md-table-cell">Categoria</th>
                  </tr>
                </thead>
                <tbody>
                  {clientes.map((cli) => (
                    <tr
                      key={cli.public_id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => aoEscolher({ public_id: cli.public_id, nome: cli.c1_nome, celular: cli.c1_celular })}
                    >
                      <td>{destacar(cli.c1_nome)}</td>
                      <td className="text-nowrap">{cli.c1_celular || cli.c1_telefone}</td>
                      <td className="d-none d-md-table-cell">{destacar(cli.c1_email)}</td>
                      <td className="d-none d-md-table-cell">{cli.c1_categoria}</td>
                    </tr>
                  ))}
                  {clientes.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-muted">
                        Nenhum cliente encontrado. Use "Novo cliente" para cadastrar.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={() => setNovo(true)}>
              Novo cliente
            </Button>
            <Button variant="secondary" onClick={aoFechar}>
              Fechar
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={salvarNovo}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={7}>
                <Form.Group controlId="novo-nome">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required autoFocus />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group controlId="novo-celular">
                  <Form.Label>Telefone</Form.Label>
                  <Form.Control value={form.celular} onChange={(e) => setForm({ ...form, celular: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={7}>
                <Form.Group controlId="novo-email">
                  <Form.Label>E-mail</Form.Label>
                  <Form.Control type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group controlId="novo-categoria">
                  <Form.Label>Categoria</Form.Label>
                  <Form.Select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                    <option>Comprador interessado</option>
                    <option>Proprietário vendedor</option>
                    <option>Prospecção</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="checkbox"
                  id="novo-whats"
                  label="Possui WhatsApp"
                  checked={form.whats}
                  onChange={(e) => setForm({ ...form, whats: e.target.checked })}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button type="submit" variant="success" disabled={salvando}>
              {salvando ? <Spinner animation="border" size="sm" /> : 'Salvar'}
            </Button>
            <Button variant="secondary" onClick={() => setNovo(false)}>
              Voltar
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
}
